import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

export interface NoteLinkOptions {
  HTMLAttributes: Record<string, unknown>;
  openOnClick: boolean;
  onNavigate?: (noteId: string) => void;
}

function extractNoteIdFromHref(href: string): string | null {
  const trimmed = (href || '').trim();
  if (!trimmed) {
    return null;
  }

  // Support both relative and absolute URLs and both /view/:id and /view2/:id.
  const match = trimmed.match(/(?:^|\/)view(?:2)?\/([^/?#]+)/i);
  if (!match?.[1]) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    noteLink: {
      /**
       * Set a note link
       */
      setNoteLink: (noteId: string, title?: string) => ReturnType;
      /**
       * Unset a note link
       */
      unsetNoteLink: () => ReturnType;
    };
  }
}

export const NoteLink = Mark.create<NoteLinkOptions>({
  name: 'noteLink',

  priority: 1000,

  inclusive: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      openOnClick: true,
      onNavigate: undefined,
    };
  },

  addAttributes() {
    return {
      noteId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-note-id'),
        renderHTML: (attributes) => {
          if (!attributes['noteId']) {
            return {};
          }
          return {
            'data-note-id': attributes['noteId'],
          };
        },
      },
      href: {
        default: null,
        parseHTML: (element) => element.getAttribute('href'),
        renderHTML: (attributes) => {
          if (!attributes['noteId']) {
            return {};
          }
          // Generate href from noteId for fallback
          return {
            href: `/view/${attributes['noteId']}`,
          };
        },
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-title'),
        renderHTML: (attributes) => {
          if (!attributes['title']) {
            return {};
          }
          return {
            'data-title': attributes['title'],
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-type="note-link"]',
        getAttrs: (node) => {
          const element = node as HTMLElement;
          const noteId = element.getAttribute('data-note-id');
          return noteId ? { noteId } : false;
        },
      },
      {
        // Legacy note links from old Quill editor
        tag: 'a.note-link',
        getAttrs: (node) => {
          const element = node as HTMLElement;
          const noteId = element.getAttribute('data-note-id');
          // For legacy links, also try to extract noteId from href if data-note-id is missing
          if (!noteId && element.hasAttribute('href')) {
            const extractedId = extractNoteIdFromHref(element.getAttribute('href') || '');
            if (extractedId) {
              return { noteId: extractedId };
            }
          }
          return noteId ? { noteId } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(
        {
          'data-type': 'note-link',
          class: 'text-blue-600 underline hover:text-blue-800 cursor-pointer transition-colors',
        },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      0,
    ];
  },

  addCommands() {
    return {
      setNoteLink:
        (noteId: string, title?: string) =>
        ({ commands }) => {
          const attributes: Record<string, string> = { noteId };
          if (title) {
            attributes['title'] = title;
          }
          return commands.setMark(this.name, attributes);
        },
      unsetNoteLink:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },

  addProseMirrorPlugins() {
    const { editor } = this;
    const onNavigate = this.options.onNavigate;

    return [
      new Plugin({
        key: new PluginKey('noteLinkHandler'),
        props: {
          handleClick(view, pos, event) {
            if (!editor.options.editable && event.button === 0) {
              // In read-only mode, handle all clicks
              const target = event.target as HTMLElement;
              // Handle both new links (a[data-type="note-link"]) and legacy links (a.note-link)
              const link = target.closest('a[data-type="note-link"]') || target.closest('a.note-link');
              
              if (link) {
                const noteId =
                  link.getAttribute('data-note-id') ||
                  extractNoteIdFromHref(link.getAttribute('href') || '');
                
                if (noteId && onNavigate) {
                  event.preventDefault();
                  event.stopPropagation();
                  onNavigate(noteId);
                  return true;
                }
              }
            } else if (editor.options.editable && (event.metaKey || event.ctrlKey)) {
              // In edit mode, only handle Ctrl/Cmd + Click
              const target = event.target as HTMLElement;
              // Handle both new links (a[data-type="note-link"]) and legacy links (a.note-link)
              const link = target.closest('a[data-type="note-link"]') || target.closest('a.note-link');
              
              if (link) {
                const noteId =
                  link.getAttribute('data-note-id') ||
                  extractNoteIdFromHref(link.getAttribute('href') || '');
                
                if (noteId && onNavigate) {
                  event.preventDefault();
                  event.stopPropagation();
                  onNavigate(noteId);
                  return true;
                }
              }
            }
            
            return false;
          },
        },
      }),
    ];
  },
});
